'use client'

import { useState, useRef, useEffect, useLayoutEffect } from 'react'

import styles from './ShareBtn.module.scss'
import ShareElements from './ShareElements'
import Icon from '@/components/Icon/Icon'
import { Link } from '@/components/Link'
import { clsx } from 'clsx'

export default function ShareBtn({ url }: { url: string }) {
  const [tooltip, setTooltip] = useState({
    open: false,
    x: 0,
    y: 0,
    trigger: null as HTMLElement | null,
  })
  const tooltipRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [placementClass, setPlacementClass] = useState('')

  const updatePosition = (el: any, open?: boolean) => {
    if (!el) return

    const rect = el.getBoundingClientRect()

    setTooltip((t) => ({
      open: open ?? t.open,
      trigger: el,
      x: rect.left + rect.width / 2,
      y: rect.top,
    }))
  }

  const handleClick = (e: any) => {
    updatePosition(e.currentTarget, !tooltip.open)
  }

  useEffect(() => {
    const handler = (e: any) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setTooltip((t) => ({ ...t, open: false }))
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useLayoutEffect(() => {
    if (tooltip.open && tooltipRef.current) {
      const rect = tooltipRef.current.getBoundingClientRect()
      if (rect.left <= 20) {
        setPlacementClass('is-left-edge')
      } else if (rect.right >= window.innerWidth - 20) {
        setPlacementClass('is-right-edge')
      } else {
        setPlacementClass('')
      }
    }
  }, [tooltip])

  return (
    <div className={styles['share-container']} ref={containerRef}>
      <div className={clsx(styles['tooltip-wrapper'], styles['share'])}>
        <button
          className={clsx(styles['share-btn'], styles['with-text'])}
          aria-label="Teilen"
          onClick={(e) => handleClick(e)}
        >
          <Icon name="share" size={24} className={styles.icon} />
          <div className={clsx(styles['share-label'], styles['with-text'])}>Teilen</div>
        </button>
        {tooltip.open && (
          <div
            className={clsx(styles.tooltip, styles[placementClass])}
            style={{
              position: 'absolute',
              bottom: `calc(100% + 10px)`,
            }}
            ref={tooltipRef}
          >
            <div className={clsx(styles['tooltip-arrow'], styles[placementClass])} />
            <div className={styles['tooltip-head']}>
              <span className={styles['tooltip-title']}>Teilen</span>
              <div
                className={styles['close-btn']}
                aria-label="Schließen"
                onClick={() => setTooltip((t) => ({ ...t, open: false }))}
              >
                <Icon name="close" size={24} />
              </div>
            </div>
            <ShareElements url={url} />
          </div>
        )}
      </div>

      <Link
        href="https://www.google.de/preferences/source?q=n-tv.de"
        title="Auf Google News folgen"
        target="_blank"
        className={clsx(styles['share-btn'], styles['with-text'], styles['google-news'])}
      >
        <Icon name="starCard" size={24} />
        <div className={clsx(styles['share-label'], styles['with-text'], styles['underlined'])}>
          ntv bei Google bevorzugen
        </div>
      </Link>
    </div>
  )
}
